import React, { useState } from "react";
import styled from "styled-components";
import {
  useGetBalanceQuery,
  useGetProfileQuery,
  useCreateWithdrawalMutation,
  useGetWithdrawalsQuery,
  useGetBalanceHistoryQuery,
} from "../store/api";
import { WithdrawalStatus } from "../types/api";

const BalanceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const BalanceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const BalanceCard = styled.div`
  background: var(--bg-card);
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
  border: 1px solid var(--border-color);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const CardTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const BalanceAmount = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const BalanceSubtext = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-primary);
  border-radius: 0.75rem 0.75rem 0 0;
  overflow-x: auto;
`;

const Tab = styled.button.withConfig({
  shouldForwardProp: (prop) => !["active"].includes(prop),
})<{ active: boolean }>`
  padding: 1rem 1.5rem;
  border: none;
  background: ${(props) => (props.active ? "var(--bg-card)" : "transparent")};
  color: ${(props) => (props.active ? "var(--text-primary)" : "var(--text-muted)")};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border-bottom: 2px solid
    ${(props) => (props.active ? "var(--accent-primary)" : "transparent")};
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    color: var(--text-primary);
    background: ${(props) =>
      props.active ? "var(--bg-card)" : "var(--bg-hover)"};
  }
`;

const TabContent = styled.div`
  background: var(--bg-card);
  border-radius: 0 0 0.75rem 0.75rem;
  padding: 1.5rem;
  border: 1px solid var(--border-color);
  border-top: none;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  padding: 2rem;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-muted);

  &:hover {
    color: var(--text-primary);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-input);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--border-input-focus);
    box-shadow: var(--focus-ring);
  }

  &:invalid {
    border-color: var(--accent-error);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;

const Button = styled.button.withConfig({
  shouldForwardProp: (prop) => !["variant"].includes(prop),
})<{ variant?: "primary" | "secondary" }>`
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  ${({ variant }) =>
    variant === "primary"
      ? `
    background: var(--accent-primary);
    color: white;
    border: none;
    
    &:hover {
      background: var(--accent-primary-hover);
    }
  `
      : `
    background: var(--bg-card);
    color: var(--text-primary);
    border: 1px solid var(--border-input);
    
    &:hover {
      background: var(--bg-primary);
    }
  `}

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const WithdrawButton = styled.button`
  background: var(--accent-primary);
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--accent-primary-hover);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.div`
  color: var(--accent-error);
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const SuccessText = styled.div`
  color: var(--accent-primary);
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const HelperText = styled.div`
  font-size: 0.875rem;
  color: var(--text-muted);
  margin-top: 0.5rem;
`;

const StatusBadge = styled.span.withConfig({
  shouldForwardProp: (prop) => !["status"].includes(prop),
})<{ status: WithdrawalStatus }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;

  ${({ status }) => {
    switch (status) {
      case WithdrawalStatus.PENDING:
        return `
          background: var(--status-pending-bg);
          color: var(--status-pending-text);
        `;
      case WithdrawalStatus.APPROVED:
        return `
          background: var(--status-processing-bg);
          color: var(--status-processing-text);
        `;
      case WithdrawalStatus.COMPLETED:
        return `
          background: var(--status-success-bg);
          color: var(--status-success-text);
        `;
      case WithdrawalStatus.REJECTED:
        return `
          background: var(--status-error-bg);
          color: var(--status-error-text);
        `;
      case WithdrawalStatus.CANCELLED:
        return `
          background: var(--bg-secondary);
          color: var(--text-primary);
        `;
      default:
        return `
          background: var(--bg-secondary);
          color: var(--text-primary);
        `;
    }
  }}
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
`;

const TableHeader = styled.th`
  padding: 0.75rem;
  text-align: left;
  font-weight: 600;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-divider);
  background: var(--bg-primary);
  font-size: 0.875rem;
`;

const TableCell = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid var(--border-divider);
  font-size: 0.875rem;
  color: var(--text-primary);
`;

const TableRow = styled.tr`
  &:hover {
    background: var(--bg-primary);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--text-muted);
`;

function Balance() {
  const [activeTab, setActiveTab] = useState<"history" | "withdrawals">(
    "history"
  );
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({
    amount: "",
    walletAddress: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const { data: balance } = useGetBalanceQuery();
  const { data: profile } = useGetProfileQuery();
  const { data: withdrawalsData } = useGetWithdrawalsQuery({
    page: 1,
    limit: 10,
  });
  const { data: balanceHistoryData } = useGetBalanceHistoryQuery({
    page: 1,
    limit: 10,
  });
  const [createWithdrawal, { isLoading: isCreating }] =
    useCreateWithdrawalMutation();

  const merchant = profile?.merchant;
  const withdrawals = withdrawalsData?.data || [];
  const balanceHistory = Array.isArray(balanceHistoryData)
    ? balanceHistoryData
    : (balanceHistoryData as any)?.data || [];

  const formatUSDT = (amount: number) => {
    return `${amount.toFixed(2)} USDT`;
  };

  const currentBalance = balance?.balance || 0;
  // frozenBalance включает активные ордера и pending/approved заявки на вывод (см. backend BalanceService.getUserBalance).
  const frozenBalance = balance?.frozenBalance || 0;
  const availableBalance = Math.max(0, currentBalance - frozenBalance);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setWithdrawForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleWithdrawSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!merchant?.id) {
      setError("Информация о мерчанте не найдена");
      return;
    }

    const amount = parseFloat(withdrawForm.amount);
    if (!amount || amount <= 0) {
      setError("Введите корректную сумму");
      return;
    }

    if (amount > availableBalance) {
      setError("Недостаточно средств на балансе");
      return;
    }

    if (!withdrawForm.walletAddress.match(/^T[A-Za-z1-9]{33}$/)) {
      setError(
        "Введите корректный TRC-20 адрес (должен начинаться с T и содержать 34 символа)"
      );
      return;
    }

    try {
      await createWithdrawal({
        amount: amount,
        currency: "USDT",
        walletAddress: withdrawForm.walletAddress,
      }).unwrap();

      setSuccess("Заявка на вывод успешно создана!");
      setWithdrawForm({
        amount: "",
        walletAddress: "",
      });
      setTimeout(() => {
        setShowWithdrawModal(false);
        setSuccess("");
      }, 2000);
    } catch (err: any) {
      setError(err?.data?.message || "Ошибка при создании заявки на вывод");
    }
  };

  const getStatusText = (status: WithdrawalStatus) => {
    switch (status) {
      case WithdrawalStatus.PENDING:
        return "В обработке";
      case WithdrawalStatus.APPROVED:
        return "Одобрена";
      case WithdrawalStatus.COMPLETED:
        return "Завершена";
      case WithdrawalStatus.REJECTED:
        return "Отклонена";
      case WithdrawalStatus.CANCELLED:
        return "Отменена";
      default:
        return status;
    }
  };

  const renderBalanceHistory = () => (
    <Table>
      <thead>
        <tr>
          <TableHeader>ID</TableHeader>
          <TableHeader>Создан</TableHeader>
          <TableHeader>До</TableHeader>
          <TableHeader>Сумма</TableHeader>
          <TableHeader>После</TableHeader>
          <TableHeader>Описание</TableHeader>
        </tr>
      </thead>
      <tbody>
        {balanceHistory.length > 0 ? (
          balanceHistory.map((item: any) => (
            <TableRow key={item.id}>
              <TableCell>{item.id.slice(0, 8)}</TableCell>
              <TableCell>
                {new Date(item.createdAt).toLocaleDateString("ru-RU", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </TableCell>
              <TableCell>{formatUSDT(item.balanceBefore || 0)}</TableCell>
              <TableCell
                style={{
                  color: item.amount >= 0 ? "#194BFF" : "#ef4444",
                }}
              >
                {item.amount >= 0 ? "+" : ""}
                {formatUSDT(item.amount || 0)}
              </TableCell>
              <TableCell>{formatUSDT(item.balanceAfter || 0)}</TableCell>
              <TableCell>{item.description || "—"}</TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={6}>
              <EmptyState>История операций пуста</EmptyState>
            </TableCell>
          </TableRow>
        )}
      </tbody>
    </Table>
  );

  const renderWithdrawals = () => (
    <Table>
      <thead>
        <tr>
          <TableHeader>ID</TableHeader>
          <TableHeader>Создан</TableHeader>
          <TableHeader>Исполнен</TableHeader>
          <TableHeader>Сумма</TableHeader>
          <TableHeader>Статус</TableHeader>
        </tr>
      </thead>
      <tbody>
        {withdrawals.length > 0 ? (
          withdrawals.map((withdrawal: any) => (
            <TableRow key={withdrawal.id}>
              <TableCell>{withdrawal.id.substring(0, 8)}</TableCell>
              <TableCell>
                {new Date(withdrawal.createdAt).toLocaleDateString("ru-RU")}
              </TableCell>
              <TableCell>
                {withdrawal.completedAt
                  ? new Date(withdrawal.completedAt).toLocaleDateString("ru-RU")
                  : "—"}
              </TableCell>
              <TableCell>{formatUSDT(withdrawal.amount)}</TableCell>
              <TableCell>
                <StatusBadge status={withdrawal.status}>
                  {getStatusText(withdrawal.status)}
                </StatusBadge>
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={5}>
              <EmptyState>Нет выводов</EmptyState>
            </TableCell>
          </TableRow>
        )}
      </tbody>
    </Table>
  );

  return (
    <BalanceContainer>
      <BalanceGrid>
        <BalanceCard>
          <CardHeader>
            <CardTitle>Доступный баланс к выводу</CardTitle>
            <WithdrawButton onClick={() => setShowWithdrawModal(true)}>
              Вывести USDT
            </WithdrawButton>
          </CardHeader>
          <BalanceAmount>{formatUSDT(availableBalance)}</BalanceAmount>
          <BalanceSubtext>Доступно к использованию</BalanceSubtext>
        </BalanceCard>
      </BalanceGrid>

      {/* Табы для переключения между историей и выводами */}
      <div>
        <TabsContainer>
          <Tab
            active={activeTab === "history"}
            onClick={() => setActiveTab("history")}
          >
            История баланса
          </Tab>
          <Tab
            active={activeTab === "withdrawals"}
            onClick={() => setActiveTab("withdrawals")}
          >
            История выводов
          </Tab>
        </TabsContainer>

        <TabContent>
          {activeTab === "history" && renderBalanceHistory()}
          {activeTab === "withdrawals" && renderWithdrawals()}
        </TabContent>
      </div>

      {showWithdrawModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Вывод USDT</ModalTitle>
              <CloseButton onClick={() => setShowWithdrawModal(false)}>
                ×
              </CloseButton>
            </ModalHeader>

            <form onSubmit={handleWithdrawSubmit}>
              <FormGroup>
                <Label>Сумма для вывода (USDT)</Label>
                <Input
                  type="number"
                  name="amount"
                  value={withdrawForm.amount}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  max={availableBalance}
                  required
                />
                <HelperText>
                  Доступно: {formatUSDT(availableBalance)}
                </HelperText>
                <HelperText style={{ color: "#ef4444", fontWeight: "500" }}>
                  Фиксированная комиссия за вывод: 5 USDT
                </HelperText>
              </FormGroup>

              <FormGroup>
                <Label>TRC-20 адрес кошелька</Label>
                <Input
                  type="text"
                  name="walletAddress"
                  value={withdrawForm.walletAddress}
                  onChange={handleInputChange}
                  placeholder="TRBLNv6JKPCdYtXoYYvKMJhcBSjnBFgFyJ"
                  pattern="^T[A-Za-z1-9]{33}$"
                  required
                />
                <HelperText>
                  Адрес должен начинаться с T и содержать 34 символа
                </HelperText>
              </FormGroup>

              {error && <ErrorText>{error}</ErrorText>}
              {success && <SuccessText>{success}</SuccessText>}

              <ButtonGroup>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowWithdrawModal(false)}
                >
                  Отмена
                </Button>
                <Button type="submit" variant="primary" disabled={isCreating}>
                  {isCreating ? "Создание..." : "Создать заявку"}
                </Button>
              </ButtonGroup>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}
    </BalanceContainer>
  );
}

export default Balance;
